// src/pages/admin/AdminUsers.jsx
import React, { useEffect, useState } from "react";
import { getAllUsers } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import { FiSearch, FiUser, FiChevronLeft, FiChevronRight, FiUsers } from "react-icons/fi";

export default function AdminUsers() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    getAllUsers().then((u) => { setUsers(u); setLoading(false); });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filtered = users.filter((u) =>
    (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.phone || "").includes(search)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  // Compact page-number list with ellipses, e.g. 1 … 4 5 [6] 7 8 … 12
  const getPageNumbers = () => {
    const pages = [];
    const windowSize = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= safePage - windowSize && i <= safePage + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "…") {
        pages.push("…");
      }
    }
    return pages;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">View and manage registered customers</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold border border-primary-100">
          <FiUsers className="w-4 h-4" /> {users.length} registered
        </span>
      </div>

      <div className="relative mb-4 max-w-sm">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone..." className="input pl-9 py-2 text-sm" />
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden border border-gray-200 rounded-xl shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["User", "Email", "Phone", "Role", "Joined"].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16">
                      <div className="flex flex-col items-center justify-center text-gray-400 gap-2">
                        <FiUser className="w-8 h-8" />
                        <p className="text-sm font-medium text-gray-500">No users found</p>
                        <p className="text-xs text-gray-400">Try adjusting your search</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((user, idx) => (
                    <tr key={user.id} className={`transition-colors hover:bg-primary-50/40 ${idx % 2 === 1 ? "bg-gray-50/50" : "bg-white"}`}>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold flex-shrink-0">
                            {(user.name || user.email || "U")[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">{user.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-gray-600">{user.email}</td>
                      <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{user.phone || "—"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block text-xs font-medium px-2 py-1 rounded-md border ${user.role === "admin" ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
                          {user.role || "customer"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">
                        {user.createdAt?.toDate ? format(user.createdAt.toDate(), "dd MMM yyyy") : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination footer ── */}
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{startIdx + 1}</span>–
                <span className="font-medium text-gray-700">{Math.min(startIdx + PAGE_SIZE, filtered.length)}</span> of{" "}
                <span className="font-medium text-gray-700">{filtered.length}</span> users
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronLeft className="w-4 h-4" />
                </button>

                {getPageNumbers().map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`min-w-[2rem] h-8 px-2 rounded-lg text-xs font-medium border transition-colors ${p === safePage ? "bg-primary-600 text-white border-primary-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {p}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}